import React from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from "recharts";
import PanelShell from "../../shared/PanelShell";
import ThemedChartTooltip from "../../shared/ThemedChartTooltip";
import { SkeletonChart } from "../../shared/Skeleton";
import { fmtNumber } from "../../shared/format";

// GREETING LOSS — Tier 3, Phone only (V6 §E.5). histogram of buckets[] with
// pct_never_spoke overlaid + the three greeting averages. There is no greeting to
// abandon on chat, so this panel is never mounted there. Lifted verbatim from the
// v1 CoreTier so it survives that container's teardown.
//
// The bucket rows are {bucket, label, calls, never_spoke, pct_never_spoke} — the
// bar reads `calls`, not `count` (reading `count` drew nothing and left only the
// red never-spoke bars). The three averages are the content: the difference between
// the greeting the callers who LEFT sat through and the one those who SPOKE heard is
// the reader's to draw, not this component's.
const GreetingLossPanel = ({ panelKey = "voice_greeting_loss", say, callLoss, loading = false }) => {
  const buckets = Array.isArray(callLoss?.buckets) ? callLoss.buckets : [];
  const avgs = [
    { key: "all", label: "All callers", value: callLoss?.avg_greeting_sec_all },
    { key: "engaged", label: "Who spoke", value: callLoss?.avg_greeting_sec_engaged },
    { key: "abandoned", label: "Who left", value: callLoss?.avg_greeting_sec_abandoned },
  ].filter((a) => a.value != null);
  return (
    <PanelShell
      panelKey={panelKey}
      label="Greeting loss"
      say={say}
      hasData={buckets.length > 0 || avgs.length > 0}
      loading={loading}
      className="flex flex-col"
    >
      {loading && !callLoss ? <SkeletonChart height={240} /> : (
        <div className="grow h-[240px] w-full" data-testid="greeting-loss-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={buckets} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-800" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} className="fill-gray-500" />
              <YAxis tick={{ fontSize: 11 }} className="fill-gray-500" />
              <ThemedChartTooltip />
              {/* DEFERRED to fast-follow (V6 §E.5): the spec's DualAxisBandChart plots
                  pct_never_spoke on a SECOND axis, not never_spoke as a second count
                  bar. This grouped-bar form conveys the same two facts; converting the
                  overlay to a right-axis percentage is tracked, proportionate work. */}
              <Bar dataKey="calls" name="Calls" fill="#32B1FF" />
              <Bar dataKey="never_spoke" name="Never spoke" fill="#DC2626" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {avgs.length > 0 && (
        <div data-testid="greeting-averages" className="flex flex-wrap gap-x-6 gap-y-1 mt-3">
          {avgs.map((a) => (
            <div key={a.key} data-avg={a.key} className="flex items-baseline gap-1.5">
              <span className="gi-body font-medium text-gray-900 dark:text-gray-100 tabular-nums">
                {`${fmtNumber(a.value, true)}s`}
              </span>
              <span className="gi-label text-gray-500 dark:text-gray-400 normal-case tracking-normal">
                {a.label}
              </span>
            </div>
          ))}
          <p className="gi-label text-gray-500 dark:text-gray-400 normal-case tracking-normal basis-full">
            Average greeting heard before the caller spoke or hung up.
          </p>
        </div>
      )}
    </PanelShell>
  );
};

export default GreetingLossPanel;
